import { MRT_ColumnDef } from "material-react-table"
import { useMemo } from "react"
import { ReporteVentaItem } from "../interfaces/erp/reporte-venta/reporte-venta.interface"
import { cellNotFilter } from "../utils"
import { Chip } from "@mui/material"
import { format } from "date-fns"

interface UseTableColumnsReporteVentaProps {
  pagination?: { pageIndex: number; pageSize: number };
}

export const useTableColumnsReporteComisiones = (props?: UseTableColumnsReporteVentaProps) => {
  const { pagination } = props || {};

  const columnBase = useMemo<MRT_ColumnDef<ReporteVentaItem>[]>(() => [
    {
      accessorKey: 'numero',
      header: '#',
      size: 60,
      enableColumnFilter: false,
      Cell: ({ row }) => {
        // Calcular número absoluto basado en la página actual
        const baseNumber = pagination ? (pagination.pageIndex * pagination.pageSize) : 0;
        return baseNumber + row.index + 1;
      },
    },
    {
      accessorKey: 'estado_registro',
      header: 'Estado',
      size: 140,
      Cell: ({ cell }) => {
        const estado = cell.getValue<string>();
        if (!estado || estado === '-') return '-';
        
        const isActivo = estado === 'Activo';
        return (
          <Chip
            label={isActivo ? 'Activo' : 'Pendiente Revisión'}
            color={isActivo ? 'success' : 'warning'}
            size="small"
            sx={{ fontWeight: 600 }}
          />
        );
      },
    },
    {
      accessorKey: 'tipo',
      header: 'Vendedor',
      size: 120,
      Cell: ({ cell }) => cell.getValue<string>() || '-',
    },
    {
      accessorKey: 'vendedor',
      header: 'Perfil Vendedor',
      size: 200,
      Cell: ({ cell }) => cell.getValue<string>() || '-',
    },
    {
      accessorKey: 'cargo',
      header: 'Grupo de Ventas',
      size: 150,
      Cell: ({ cell }) => cell.getValue<string>() || '-',
    },
    {
      ...cellNotFilter,
      accessorKey: 'fecha',
      header: 'Fecha Instalación',
      size: 150,
      Cell: ({ cell }) => {
        const fecha = cell.getValue() as string;
        if (fecha) {
          try {
            return format(new Date(fecha), 'dd/MM/yyyy');
          } catch {
            return fecha;
          }
        }
        return '-';
      },
    },
    {
      accessorKey: 'nombre_cliente',
      header: 'Subido el Contrato',
      size: 300,
      Cell: ({ cell }) => cell.getValue<string>() || '-',
    },
    {
      accessorKey: 'ciudad',
      header: 'No. Contrato',
      size: 150,
      Cell: ({ cell }) => cell.getValue<string>() || '-',
    },
    {
      accessorKey: 'sector',
      header: 'Cliente',
      size: 150,
      Cell: ({ cell }) => cell.getValue<string>() || '-',
    },
    {
      ...cellNotFilter,
      accessorKey: 'coordenadas',
      header: '# Documento',
      size: 180,
      Cell: ({ cell }) => {
        const documento = cell.getValue<string>();
        return documento || '-';
      },
    },
    {
      accessorKey: 'tecnico',
      header: 'Sector',
      size: 150,
      Cell: ({ cell }) => cell.getValue<string>() || '-',
    },
    {
      accessorKey: 'forma_pago',
      header: 'Forma Pago',
      size: 130,
      Cell: ({ cell }) => cell.getValue<string>() || '-',
    },
    {
      accessorKey: 'Banco',
      header: 'Buró de Crédito',
      size: 150,
      Cell: ({ cell }) => {
        const banco = cell.getValue<string>();
        return banco || '-';
      },
    },
    {
      accessorKey: 'estado_venta',
      header: 'Ciudad',
      size: 150,
      Cell: ({ cell }) => {
        const estado = cell.getValue() as string;
        const colorMap: Record<string, 'success' | 'warning' | 'error' | 'info' | 'default'> = {
          'Exitosa': 'success',
          'Pendiente': 'warning',
          'Fallido': 'error',
          'En Proceso': 'info',
        };
        return (
          <Chip
            label={estado || '-'}
            color={colorMap[estado] || 'default'}
            size="small"
          />
        );
      },
    },
    {
      accessorKey: 'estado_instalacion',
      header: 'Plan',
      size: 160,
      Cell: ({ cell }) => {
        const estado = cell.getValue() as string;
        const colorMap: Record<string, 'success' | 'warning' | 'error' | 'info' | 'default'> = {
          'completado': 'success',
          'ok': 'success',
          'en proceso': 'warning',
          'fallido': 'error',
          'pendiente': 'info',
        };
        return estado ? (
          <Chip
            label={estado}
            color={colorMap[estado.toLowerCase()] || 'default'}
            size="small"
          />
        ) : '-';
      },
    },
    {
      accessorKey: 'nombre_plan_factura',
      header: 'Costo sin IVA/SEGURO',
      size: 150,
      Cell: ({ cell }) => {
        const planComercial = cell.getValue<string>();
        return planComercial || '-';
      },
    },
    {
      accessorKey: 'nombre_plan',
      header: 'Servicio',
      size: 150,
      Cell: ({ cell }) => {
        const plan = cell.getValue() as string;
        return plan ? (
          <Chip
            label={plan}
            size="small"
            variant="outlined"
            color="primary"
          />
        ) : '-';
      },
    },
    {
      accessorKey: 'tipo_plan',
      header: 'Estado Cliente',
      size: 130,
      Cell: ({ cell }) => {
        const tipoPlan = cell.getValue() as string;
        const colorMap: Record<string, 'success' | 'warning' | 'error' | 'info' | 'default'> = {
          'Home': 'info',
          'Corporativo': 'success',
          'Pymes': 'warning',
          'ISP': 'error',
        };
        return tipoPlan ? (
          <Chip
            label={tipoPlan}
            color={colorMap[tipoPlan] || 'default'}
            size="small"
          />
        ) : '-';
      },
    },
    {
      accessorKey: 'valor_mensual',
      header: 'Porcentaje Comisión',
      size: 130,
      Cell: ({ cell }) => {
        const valor = cell.getValue();
        return valor ? `${Number(valor).toFixed(2)} %` : '-';
      },
    },
    {
      accessorKey: 'valor_instalacion',
      header: 'Valor',
      size: 150,
      Cell: ({ cell }) => {
        const valor = cell.getValue();
        return valor ? `$${Number(valor).toFixed(2)}` : '-';
      },
    },
  ], [pagination]);

  return {
    columnBase
  }
}
